import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Table,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { DisableForViewer } from "../../components/common/DisableForViewer";
import { useIsViewer } from "../../hooks/useIsViewer";
import {
  addEmulatorTransaction,
  type ActiveTransaction,
  type AddTransactionRequest,
  getActiveTransactions,
  stopTransaction,
} from "../../services/emulator/api";
import { getSiteOptions } from "../../services/client/@tanstack/react-query.gen";
import { DATE_TIME_FORMAT, TIME_FORMAT } from "../../util/formatDate";

const { Title } = Typography;

type FormValues = {
  asset_id: string;
  transaction_id: string;
  start_energy: number;
  full_battery_energy: number;
  max_power: number;
  used_charging_pins: ("pin1" | "pin2" | "pin3")[];
  start_time: dayjs.Dayjs;
  stop_time: dayjs.Dayjs;
  requested_min_energy?: number;
  requested_max_energy?: number;
  estimated_departure_time?: dayjs.Dayjs | null;
  priority: number;
  badge_id?: string;
};

const CHARGING_PIN_OPTIONS = [
  { label: "Pin 1", value: "pin1" },
  { label: "Pin 2", value: "pin2" },
  { label: "Pin 3", value: "pin3" },
];

const getInitialValues = () => ({
  transaction_id: crypto.randomUUID(),
  start_time: dayjs(),
  stop_time: dayjs().add(6, "hour"),
  estimated_departure_time: dayjs().add(6, "hour"),
  start_energy: 0,
  full_battery_energy: 100,
  max_power: 11,
  used_charging_pins: ["pin1", "pin2", "pin3"] as ("pin1" | "pin2" | "pin3")[],
  priority: 0,
});

export const EmulatorTransactionPage = () => {
  const { site_id } = useParams<{ site_id: string }>();
  const [form] = Form.useForm<FormValues>();
  const queryClient = useQueryClient();
  const isViewer = useIsViewer();

  const { data: site } = useQuery({
    ...getSiteOptions({
      path: { site_id: site_id! },
    }),
    enabled: !!site_id,
  });

  const { data: activeTransactions, isLoading: isLoadingActiveTransactions } =
    useQuery({
      queryKey: ["activeTransactions", site_id],
      queryFn: () => getActiveTransactions(site_id!),
      enabled: !!site_id,
    });

  const assetOptions = useMemo(() => {
    const devices = site?.configurations?.site_configuration?.device_list ?? [];
    return devices.map((d) => ({ label: d.name, value: d.name }));
  }, [site]);

  const mutation = useMutation({
    mutationFn: (body: AddTransactionRequest) =>
      addEmulatorTransaction(site_id!, body),
    onSuccess: () => {
      message.success("Transaction added successfully");
      queryClient.invalidateQueries({
        queryKey: ["activeTransactions", site_id],
      });
      form.resetFields();
      form.setFieldsValue(getInitialValues());
    },
    onError: (error: Error) => {
      message.error(error.message || "Failed to add transaction");
    },
  });

  const stopMutation = useMutation({
    mutationFn: (transactionId: string) =>
      stopTransaction(site_id!, transactionId),
    onSuccess: () => {
      message.success("Transaction stopped");
      queryClient.invalidateQueries({
        queryKey: ["activeTransactions", site_id],
      });
    },
    onError: (error: Error) => {
      message.error(error.message || "Failed to stop transaction");
    },
  });

  const activeTransactionColumns: ColumnsType<ActiveTransaction> = [
    { title: "Asset ID", dataIndex: "asset_id", key: "asset_id" },
    {
      title: "Transaction ID",
      dataIndex: "transaction_id",
      key: "transaction_id",
    },
    {
      title: "",
      key: "actions",
      render: (_, record) => (
        <DisableForViewer>
          <Button
            danger
            onClick={() => stopMutation.mutate(record.transaction_id)}
            loading={
              stopMutation.isPending &&
              stopMutation.variables === record.transaction_id
            }
          >
            Stop
          </Button>
        </DisableForViewer>
      ),
    },
  ];

  const onFinish = (values: FormValues) => {
    const { start_time, stop_time, estimated_departure_time, ...rest } = values;

    mutation.mutate({
      ...rest,
      max_power: rest.max_power * 1000,
      start_energy:
        rest.start_energy != null ? rest.start_energy * 1000 : undefined,
      full_battery_energy: rest.full_battery_energy * 1000,
      requested_min_energy:
        rest.requested_min_energy != null
          ? rest.requested_min_energy * 1000
          : undefined,
      requested_max_energy:
        rest.requested_max_energy != null
          ? rest.requested_max_energy * 1000
          : undefined,
      start_time: start_time.toISOString(),
      stop_time: stop_time.toISOString(),
      estimated_departure_time: estimated_departure_time
        ? estimated_departure_time.toISOString()
        : null,
    });
  };

  return (
    <div className="p-6 max-w-2xl">
      <Title level={3}>Emulator — {site_id}</Title>

      <Title level={5}>Active Transactions</Title>
      <Table<ActiveTransaction>
        columns={activeTransactionColumns}
        dataSource={activeTransactions}
        loading={isLoadingActiveTransactions}
        rowKey="transaction_id"
        pagination={false}
        locale={{ emptyText: "No active transactions" }}
        className="mb-8"
      />

      <Title level={5}>Add Transaction</Title>
      <Typography.Text className="block mb-6 text-gray-500">
        Add a transaction to the emulator for this site.
      </Typography.Text>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={getInitialValues()}
        disabled={isViewer}
      >
        <Form.Item
          name="asset_id"
          label="Asset ID"
          rules={[{ required: true, message: "Please select an asset ID" }]}
        >
          <Select
            showSearch
            placeholder="Select an asset"
            options={assetOptions}
            filterOption={(input, option) =>
              String(option?.label ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item
          name="transaction_id"
          label="Transaction ID"
          rules={[{ required: true, message: "Please enter a transaction ID" }]}
        >
          <Input placeholder="transaction-id" />
        </Form.Item>

        <Form.Item name="badge_id" label="Badge ID">
          <Input placeholder="badge-id" />
        </Form.Item>

        <Form.Item name="start_energy" label="Start Energy (kWh)">
          <InputNumber className="w-full" min={0} step={0.1} />
        </Form.Item>

        <Form.Item
          name="full_battery_energy"
          label="Full Battery Energy (kWh)"
          rules={[
            {
              required: true,
              message: "Please enter the full battery energy",
            },
          ]}
        >
          <InputNumber className="w-full" min={0} step={0.1} />
        </Form.Item>

        <Form.Item
          name="max_power"
          label="Max Power (kW)"
          rules={[{ required: true, message: "Please enter the max power" }]}
        >
          <InputNumber className="w-full" min={0} step={0.1} />
        </Form.Item>

        <Form.Item
          name="start_time"
          label="Start Time"
          rules={[{ required: true, message: "Please select a start time" }]}
        >
          <DatePicker
            showTime={{ format: TIME_FORMAT }}
            format={DATE_TIME_FORMAT}
            className="w-full"
          />
        </Form.Item>

        <Form.Item
          name="stop_time"
          label="Stop Time"
          rules={[{ required: true, message: "Please select a stop time" }]}
        >
          <DatePicker
            showTime={{ format: TIME_FORMAT }}
            format={DATE_TIME_FORMAT}
            className="w-full"
          />
        </Form.Item>

        <Form.Item
          name="requested_min_energy"
          label="Requested Min Energy (kWh)"
        >
          <InputNumber className="w-full" min={0} step={0.1} />
        </Form.Item>

        <Form.Item
          name="requested_max_energy"
          label="Requested Max Energy (kWh)"
        >
          <InputNumber className="w-full" min={0} step={0.1} />
        </Form.Item>

        <Form.Item
          name="estimated_departure_time"
          label="Estimated Departure Time"
        >
          <DatePicker
            showTime={{ format: TIME_FORMAT }}
            format={DATE_TIME_FORMAT}
            className="w-full"
          />
        </Form.Item>

        <Form.Item name="used_charging_pins" label="Used Charging Pins">
          <Checkbox.Group options={CHARGING_PIN_OPTIONS} />
        </Form.Item>

        <Form.Item name="priority" label="Priority">
          <InputNumber className="w-full" />
        </Form.Item>

        <Form.Item>
          <DisableForViewer>
            <Button
              type="primary"
              htmlType="submit"
              loading={mutation.isPending}
              className="w-full"
            >
              Add Transaction
            </Button>
          </DisableForViewer>
        </Form.Item>
      </Form>
    </div>
  );
};

export default EmulatorTransactionPage;
